import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseQueryWithReauth } from "@cointervue/shared/api";
import { HTTP_METHODS, JOBS, JOBS_ROUTES } from "@cointervue/shared/constants";
import { logout } from "../../store/authSlice";
import type {
  CreateJobRoleRequest,
  CreateJobRoleResponse,
  CloneJobRoleRequest,
  CloneJobRoleResponse,
} from "./types";

export const createJobRoleApi = createApi({
  reducerPath: "createJobRoleApi",
  baseQuery: createBaseQueryWithReauth(JOBS.href, logout),
  tagTypes: ["JobRole"],

  endpoints: (builder) => ({
    createJobRoleWithPrompt: builder.mutation<
      CreateJobRoleResponse,
      CreateJobRoleRequest
    >({
      query: (requestBody) => ({
        url: JOBS_ROUTES.createJobRoleWithPrompt,
        method: HTTP_METHODS.POST,
        headers: {
          accept: "*/*",
          "Content-Type": "application/json",
        },
        body: requestBody,
      }),
      invalidatesTags: [{ type: "JobRole", id: "LIST" }],
    }),

    createJobRoleWithContent: builder.mutation<
      CreateJobRoleResponse,
      CreateJobRoleRequest
    >({
      query: (requestBody) => ({
        url: JOBS_ROUTES.createJobRoleWithContent,
        method: HTTP_METHODS.POST,
        headers: {
          accept: "*/*",
          "Content-Type": "application/json",
        },
        body: requestBody,
      }),
      invalidatesTags: [{ type: "JobRole", id: "LIST" }],
    }),

    createJobRoleWithFile: builder.mutation<CreateJobRoleResponse, FormData>({
      query: (formData) => ({
        url: JOBS_ROUTES.createJobRoleWithFile,
        method: HTTP_METHODS.POST,
        body: formData,
      }),
      invalidatesTags: [{ type: "JobRole", id: "LIST" }],
    }),

    cloneJobRole: builder.query<CloneJobRoleResponse, CloneJobRoleRequest>({
      query: ({ fields, s, sortBy, token }) => ({
        url: JOBS_ROUTES.fieldsOfJobRole,
        method: HTTP_METHODS.GET,
        headers: {
          accept: "*/*",
          "Content-Type": "application/json",
        },
        params: {
          fields: Array.isArray(fields) ? fields.join(",") : fields,
          s,
          sortBy,
          ...(token && { token }),
        },
      }),
      providesTags: (result) =>
        result?.data?.jobs
          ? [
              { type: "JobRole", id: "LIST" },
              ...result.data.jobs.map((job) => ({
                type: "JobRole" as const,
                id: job.jobId,
              })),
            ]
          : [{ type: "JobRole", id: "LIST" }],
    }),
  }),
});

export const {
  useCreateJobRoleWithPromptMutation,
  useCreateJobRoleWithContentMutation,
  useCreateJobRoleWithFileMutation,
  useCloneJobRoleQuery,
} = createJobRoleApi;
